import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";

export default function BestDaysVisibilityManager() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hideBestDays, setHideBestDays] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  // Check if user is admin
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const token = await currentUser.getIdTokenResult();
        setIsAdmin(token.claims.admin === true);
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load current setting
  useEffect(() => {
    if (isAdmin) {
      loadSettings();
    }
  }, [isAdmin]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, "adminSettings", "bestDaysVisibility");
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        setHideBestDays(settingsSnap.data().hideFromPublic || false);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async () => {
    try {
      setLoading(true);
      setSaveStatus("saving");
      
      const newValue = !hideBestDays;
      const settingsRef = doc(db, "adminSettings", "bestDaysVisibility");
      
      await setDoc(settingsRef, {
        hideFromPublic: newValue,
        lastUpdated: new Date(),
        updatedBy: user.email
      });
      
      setHideBestDays(newValue);
      setSaveStatus("success");
      
      setTimeout(() => setSaveStatus(""), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🔒 Access Denied</h2>
        <p>You don't have permission to access this admin panel.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: "800px", margin: "0 auto" }}>
      <h1>📊 Best Days Visibility Settings</h1>
      <p style={{ color: "#666", marginBottom: "2rem" }}>
        Control whether "Best Days" column is visible to public users in dancer profile overview tables.
      </p>

      <div style={{
        border: "2px solid #e0e0e0",
        borderRadius: "8px",
        padding: "1.5rem",
        backgroundColor: "#f9f9f9"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1rem"
        }}>
          <div>
            <h3 style={{ margin: "0 0 0.5rem 0" }}>
              Best Days Column Visibility
            </h3>
            <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
              Current status: <strong style={{ 
                color: hideBestDays ? "#dc3545" : "#28a745" 
              }}>
                {hideBestDays ? "Hidden from public" : "Visible to everyone"}
              </strong>
            </p>
          </div>
          
          <button
            onClick={toggleVisibility}
            disabled={loading}
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              backgroundColor: hideBestDays ? "#28a745" : "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: "bold",
              transition: "all 0.3s ease",
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? "⏳ Updating..." : hideBestDays ? "👁️ Show to Public" : "🙈 Hide from Public"}
          </button>
        </div>

        {saveStatus === "success" && (
          <div style={{
            padding: "0.75rem",
            backgroundColor: "#d4edda",
            color: "#155724",
            borderRadius: "4px",
            marginTop: "1rem",
            border: "1px solid #c3e6cb"
          }}>
            ✅ Settings saved successfully!
          </div>
        )}

        {saveStatus === "error" && (
          <div style={{
            padding: "0.75rem",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            borderRadius: "4px",
            marginTop: "1rem",
            border: "1px solid #f5c6cb"
          }}>
            ❌ Error saving settings. Please try again.
          </div>
        )}
      </div>

      <div style={{
        marginTop: "2rem",
        padding: "1rem",
        backgroundColor: "#fff3cd",
        border: "1px solid #ffc107",
        borderRadius: "6px"
      }}>
        <h4 style={{ margin: "0 0 0.5rem 0", color: "#856404" }}>
          ℹ️ How This Works
        </h4>
        <ul style={{ margin: 0, paddingLeft: "1.5rem", color: "#856404" }}>
          <li><strong>Hidden:</strong> Public users will see "Verified Only" placeholder in the Best Days column</li>
          <li><strong>Visible:</strong> All users can see the Best Days values for all dancers</li>
          <li><strong>Admin View:</strong> Admins can always see Best Days regardless of this setting</li>
          <li><strong>Verified Profiles:</strong> Profile owners can always see their own Best Days</li>
        </ul>
      </div>
    </div>
  );
}
